import React, { useEffect, useState } from "react";
import api from "../../Environment";

const METHOD_HELP = {
  paypal: "PayPal email or account id. Admin marks paid — no automated PayPal MassPay.",
  check: "Mailing address required. Admin mails checks offline.",
  zelle: "Email or phone handle. Admin records Zelle transfer offline.",
  stripe: "Stripe Connect account id only — no API secrets. Disbursement not automated.",
  bank_manual: "Bank name + account last4 only. Full account numbers are never stored.",
  other: "Free-form label for offline ops.",
};

/**
 * Recipient Payment / Payout Settings — Partners, Creators, Orgs, Projects, Sellers.
 * Dynamic required fields by method; incomplete profiles stay pending.
 */
function RecipientPayoutSettings({
  payableType,
  payableId,
  workspace = null,
  title = "Payment / Payout Settings",
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [meta, setMeta] = useState({
    methods: ["paypal", "check", "zelle", "stripe", "bank_manual", "other"],
    frequencies: ["manual", "weekly", "biweekly", "monthly"],
    automation_available: false,
    pending_if_incomplete: true,
  });
  const [form, setForm] = useState({
    method: "paypal",
    payout_frequency: "manual",
    paypal_email: "",
    paypal_account_id: "",
    check_payee_name: "",
    mailing_line1: "",
    mailing_line2: "",
    mailing_city: "",
    mailing_region: "",
    mailing_postal: "",
    mailing_country: "US",
    zelle_handle: "",
    stripe_account_id: "",
    stripe_onboarding_status: "",
    account_holder_name: "",
    bank_name: "",
    bank_account_last4: "",
    bank_routing_last4: "",
    label: "",
  });
  const [isComplete, setIsComplete] = useState(false);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const load = async () => {
    if (!payableType || !payableId) {
      setLoading(false);
      setNote("Link a payable entity to manage payout settings.");
      return;
    }
    setLoading(true);
    setNote("");
    try {
      const res = await api.postMethod("studio/payout/settings/get", {
        payable_type: payableType,
        payable_id: payableId,
        workspace: workspace || payableType,
      });
      const body = res && res.data ? res.data : res;
      if (!body || !body.success) {
        setNote(
          (body && (body.error_messages || body.error)) ||
            "Could not load payout settings."
        );
        setLoading(false);
        return;
      }
      const data = body.data || {};
      setMeta({
        methods: data.methods || meta.methods,
        frequencies: data.frequencies || meta.frequencies,
        automation_available: !!data.automation_available,
        pending_if_incomplete: !!data.pending_if_incomplete,
        payout_mode: data.payout_mode || "manual_record",
      });
      const d = data.destination;
      if (d) {
        const mailing = d.mailing || {};
        setForm({
          method: d.method || "paypal",
          payout_frequency: d.payout_frequency || "manual",
          paypal_email: d.paypal_email || "",
          paypal_account_id: d.paypal_account_id || "",
          check_payee_name: d.check_payee_name || "",
          mailing_line1: mailing.line1 || "",
          mailing_line2: mailing.line2 || "",
          mailing_city: mailing.city || "",
          mailing_region: mailing.region || "",
          mailing_postal: mailing.postal || "",
          mailing_country: mailing.country || "US",
          zelle_handle: d.zelle_handle || "",
          stripe_account_id: d.stripe_account_id || "",
          stripe_onboarding_status: d.stripe_onboarding_status || "",
          account_holder_name: d.account_holder_name || "",
          bank_name: d.bank_name || "",
          bank_account_last4: d.bank_account_last4 || "",
          bank_routing_last4: d.bank_routing_last4 || "",
          label: d.label || "",
        });
        setIsComplete(!!d.is_complete);
      } else {
        setIsComplete(false);
      }
    } catch (e) {
      setNote("Could not load payout settings.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payableType, payableId]);

  const save = async () => {
    if (!payableType || !payableId) return;
    setSaving(true);
    setNote("");
    try {
      const res = await api.postMethod("studio/payout/settings/save", {
        payable_type: payableType,
        payable_id: payableId,
        workspace: workspace || payableType,
        ...form,
      });
      const body = res && res.data ? res.data : res;
      if (body && body.success) {
        const d = body.data && body.data.destination;
        setIsComplete(!!(d && d.is_complete));
        setNote(
          (body.data && body.data.message) ||
            "Payout settings saved."
        );
      } else {
        setNote(
          (body && (body.error_messages || body.error)) || "Save failed."
        );
      }
    } catch (e) {
      setNote("Save failed.");
    }
    setSaving(false);
  };

  const m = form.method;

  return (
    <section className="ws-panel cstudio-panel">
      <h2>{title}</h2>
      <p className="ws-muted cstudio-muted">
        Choose how you receive payouts. Incomplete profiles stay pending until
        required fields are filled. Check and Zelle are recorded manually by
        Admin; Stripe/PayPal store account identifiers only — no automated
        disbursement in this release.
      </p>

      {loading ? (
        <p className="ws-muted">Loading…</p>
      ) : (
        <>
          <div className="ws-form-row" style={{ marginBottom: "0.75rem" }}>
            <label>
              Status:{" "}
              <strong>
                {isComplete ? "Complete" : "Incomplete — payouts pending"}
              </strong>
            </label>
          </div>
          <div className="ws-form-row" style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="payout-method">Method</label>
            <select
              id="payout-method"
              value={form.method}
              onChange={(e) => setField("method", e.target.value)}
            >
              {(meta.methods || []).map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
            <p className="ws-muted" style={{ marginTop: "0.35rem" }}>
              {METHOD_HELP[m] || ""}
            </p>
          </div>
          <div className="ws-form-row" style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="payout-freq">Payout schedule</label>
            <select
              id="payout-freq"
              value={form.payout_frequency}
              onChange={(e) => setField("payout_frequency", e.target.value)}
            >
              {(meta.frequencies || []).map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
            <p className="ws-muted" style={{ marginTop: "0.35rem" }}>
              Schedule preference for Admin ops — not an automated cron payout.
            </p>
          </div>

          {m === "paypal" && (
            <>
              <Field
                label="PayPal email"
                value={form.paypal_email}
                onChange={(v) => setField("paypal_email", v)}
                type="email"
              />
              <Field
                label="PayPal account id"
                value={form.paypal_account_id}
                onChange={(v) => setField("paypal_account_id", v)}
              />
            </>
          )}
          {m === "check" && (
            <>
              <Field
                label="Payee name"
                value={form.check_payee_name}
                onChange={(v) => setField("check_payee_name", v)}
              />
              <Field
                label="Mailing line 1"
                value={form.mailing_line1}
                onChange={(v) => setField("mailing_line1", v)}
              />
              <Field
                label="Mailing line 2"
                value={form.mailing_line2}
                onChange={(v) => setField("mailing_line2", v)}
              />
              <Field
                label="City"
                value={form.mailing_city}
                onChange={(v) => setField("mailing_city", v)}
              />
              <Field
                label="Region / state"
                value={form.mailing_region}
                onChange={(v) => setField("mailing_region", v)}
              />
              <Field
                label="Postal"
                value={form.mailing_postal}
                onChange={(v) => setField("mailing_postal", v)}
              />
              <Field
                label="Country"
                value={form.mailing_country}
                onChange={(v) => setField("mailing_country", v)}
              />
            </>
          )}
          {m === "zelle" && (
            <Field
              label="Zelle handle (email or phone)"
              value={form.zelle_handle}
              onChange={(v) => setField("zelle_handle", v)}
            />
          )}
          {m === "stripe" && (
            <>
              <Field
                label="Stripe account id"
                value={form.stripe_account_id}
                onChange={(v) => setField("stripe_account_id", v)}
              />
              <Field
                label="Onboarding status"
                value={form.stripe_onboarding_status}
                onChange={(v) => setField("stripe_onboarding_status", v)}
              />
            </>
          )}
          {m === "bank_manual" && (
            <>
              <Field
                label="Account holder"
                value={form.account_holder_name}
                onChange={(v) => setField("account_holder_name", v)}
              />
              <Field
                label="Bank name"
                value={form.bank_name}
                onChange={(v) => setField("bank_name", v)}
              />
              <Field
                label="Account last4"
                value={form.bank_account_last4}
                onChange={(v) => setField("bank_account_last4", v)}
                maxLength={4}
              />
              <Field
                label="Routing last4"
                value={form.bank_routing_last4}
                onChange={(v) => setField("bank_routing_last4", v)}
                maxLength={4}
              />
            </>
          )}
          {m === "other" && (
            <Field
              label="Label"
              value={form.label}
              onChange={(v) => setField("label", v)}
            />
          )}

          <div style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="ws-btn"
              onClick={save}
              disabled={saving || !payableType || !payableId}
            >
              {saving ? "Saving…" : "Save payout settings"}
            </button>
          </div>
          {note ? (
            <p className="ws-muted" style={{ marginTop: "0.75rem" }}>
              {note}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", maxLength }) {
  return (
    <div className="ws-form-row" style={{ marginBottom: "0.65rem" }}>
      <label>{label}</label>
      <input
        type={type}
        value={value || ""}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        style={{ display: "block", width: "100%", maxWidth: "28rem" }}
      />
    </div>
  );
}

export default RecipientPayoutSettings;
